/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */
import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import UpdateMessage from '@/core/models/ed-task-board/update-message.model';
import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import { Store } from 'vuex';
import { _HOMEWORKS_STORE } from '@/homework/values/homeworks.values';

export default class EdWboDrawerLine {
    //Indicates the id of the line the user is currently drawing or an empty string while the user is not drawing
    private curLine = null;
    private lastTime: number = performance.now(); //The time at which the last point was drawn
    private wboDrawerTools: WboDrawerTools;
    private svg: any;
    private setCursorBoardMutation: any;

    private draw: Function = (data) => {
        switch (data.type) {
            case 'straight':
                this.createLine(data);
                break;
            case 'update': {
                let line = this.svg.getElementById(data['id']);
                if (!line) {
                    this.createLine({ //create a new line in order not to loose the points
                        id: data['id'],
                        x : data['x2'],
                        y : data['y2']
                    });
                }
                this.updateLine(line, data);
                break;
            }
            default:
                break;
        }
        return true;
    }

    private continueLine: Function = (x: number, y: number, evt ?: any) => {
        /*Wait 70ms before adding any point to the currently drawing line.
        This allows the animation to be smother*/
        if (this.curLine !== null) {
            if (this.lineTool.secondary.active) {
                let alpha = Math.atan2(y - this.curLine.y, x - this.curLine.x);
                let d = Math.hypot(y - this.curLine.y, x - this.curLine.x);
                let increment = 2 * Math.PI / 16;
                alpha = Math.round(alpha / increment) * increment;
                x = this.curLine.x + d * Math.cos(alpha);
                y = this.curLine.y + d * Math.sin(alpha);
            }
            if (performance.now() - this.lastTime > 70) {
                this.wboDrawerTools.drawAndSend(new UpdateMessage(this.curLine.id, x, y));
                this.lastTime = performance.now();
            } else {
                this.draw(new UpdateMessage(this.curLine.id, x, y));
            }
        }
        if (evt) evt.preventDefault();
    }

    private startLine: Function = (x: number, y: number, evt: any) => {

        //Prevent the press from being interpreted by the browser
        evt.preventDefault();
        if(this.setCursorBoardMutation) {
            this.setCursorBoardMutation(false);
        }

        this.curLine = {
            type   : 'straight',
            id     : this.wboDrawerTools.generateUID('web_s'), //'s' for straight line
            color  : this.wboDrawerTools.getColor(),
            size   : this.wboDrawerTools.getSize(),
            opacity: this.wboDrawerTools.getOpacity(),
            x      : x,
            y      : y
        }

        this.wboDrawerTools.drawAndSend(this.curLine);
    }

    private stopLine: Function = (x: number, y: number) => {
        //Add a last point to the line
        this.continueLine(x, y);
        if (this.curLine && this.curLine.id && this.wboDrawerTools.stopStrokeCallback) {
            this.wboDrawerTools.stopStrokeCallback();
        }
        this.curLine = null;
        if(this.setCursorBoardMutation) {
            this.setCursorBoardMutation(true);
        } 
    }

    private lineTool: BoardTool = {
        name       : 'Straight line',
        shortcut   : 'l',
        listeners  : {
            press  : this.startLine,
            move   : this.continueLine,
            release: this.stopLine
        },
        secondary  : {
            name  : 'Straight line',
            icon  : '/assets/tools/line/icon-straight.svg',
            active: false
        },
        draw       : this.draw,
        mouseCursor: 'crosshair',
        icon       : '/assets/tools/line/icon.svg'
    };

    constructor(tools: WboDrawerTools, setCursorBoardMutation: any = null) {
        this.wboDrawerTools = tools;
        this.setCursorBoardMutation = setCursorBoardMutation;
        this.svg = tools.svg;
        this.wboDrawerTools.add(this.lineTool);
    }

    private createLine(lineData) {
        //Creates a new line on the canvas, or update a line that already exists with new information
        let line = this.svg.getElementById(lineData.id) || this.wboDrawerTools.createSVGElement('line');
        line.id = lineData.id;
        line.x1.baseVal.value = lineData['x'];
        line.y1.baseVal.value = lineData['y'];
        line.x2.baseVal.value = lineData['x2'] ?? lineData['x'];
        line.y2.baseVal.value = lineData['y2'] ?? lineData['y'];
        //If some data is not provided, choose default value. The line may be updated later
        line.setAttribute('stroke', lineData.color || 'black');
        line.setAttribute('stroke-width', lineData.size || 10);
        line.setAttribute('opacity', Math.max(0.1, Math.min(1, lineData.opacity)) || 1);
        lineData.transform && line.setAttribute('transform', lineData.transform);
        this.wboDrawerTools.addAnnotationOwners(line, lineData);
        this.wboDrawerTools.drawingArea.appendChild(line);
        return line;
    }

    private updateLine(line, data): void {
        line.x2.baseVal.value = data['x2'];
        line.y2.baseVal.value = data['y2'];
    }

}
